/**
 * Runtime theme builder. Merges per-tenant branding overrides (loaded from
 * `brand-config.json`) onto the canonical DeepThink theme in `./theme.ts`.
 *
 * Tenant-brandable slots: `mode`, `primary.main`, `secondary.main`,
 * `background`, `typography`. Everything else — including the DESIGN.md §4
 * semantic palette (`cta`, `selection`, `success`, `error`, `warning`,
 * `info`, `text`, `divider`) and the custom tokens (`radii`, `shadows`,
 * `gradients`)
 * — is preserved from the default theme. Those slots define the DeepThink
 * product identity; they are not white-label knobs.
 */
import { createTheme, type Theme } from '@mui/material';

import { getBrandingConfig } from './runtimeBrandConfig';
import { theme as defaultTheme } from './theme';

type MaybeBrandingConfig = ReturnType<typeof getBrandingConfig>;
type ThemeConfig = Record<string, any>;

export const buildBrandedTheme = (branding: MaybeBrandingConfig): Theme => {
  const bTheme = (branding?.theme || {}) as ThemeConfig;
  return createTheme({
    components: defaultTheme.components,
    spacing: 8,
    // `shape` is not brandable, and omitting it silently reverted the app to
    // MUI's default `borderRadius: 4` — every numeric `sx` radius in the
    // product is a multiple of this, so the shipped unit has to be the
    // declared one. Any new non-brandable key needs adding here too.
    shape: defaultTheme.shape,
    breakpoints: { values: defaultTheme.breakpoints.values },
    custom: defaultTheme.custom,
    typography: {
      ...defaultTheme.typography,
      ...(bTheme.typography || {}),
    },
    palette: {
      mode: bTheme.palette?.mode || defaultTheme.palette.mode || 'light',
      primary: {
        main:
          bTheme.palette?.primary?.main || defaultTheme.palette.primary.main,
      },
      secondary: {
        main:
          bTheme.palette?.secondary?.main ||
          defaultTheme.palette.secondary.main,
      },
      background: {
        ...defaultTheme.palette.background,
        ...(bTheme.palette?.background || {}),
      },
      // DESIGN.md §4 slots — never brandable. Preserve from defaultTheme.
      cta: defaultTheme.palette.cta,
      selection: defaultTheme.palette.selection,
      success: defaultTheme.palette.success,
      error: defaultTheme.palette.error,
      warning: defaultTheme.palette.warning,
      // Cobalt owns the informational role (§4). Left off, MUI rebuilds
      // `info` from its own light-blue default and every info Alert and
      // chip in the shipped app turns a blue `theme.ts` never declared.
      info: defaultTheme.palette.info,
      text: defaultTheme.palette.text,
      divider: defaultTheme.palette.divider,
    },
  } as any);
};
